import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Box, Button, Dialog, DialogContent, IconButton, LinearProgress,
  Popover, Snackbar, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Close, Delete, PhotoCamera, Replay, UploadFile } from '@mui/icons-material';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../shared/ConfirmDialog';
import type { ObservationMedia } from '../../types/observationMedia';
import { useFieldEvidenceOptional } from './FieldEvidenceContext';

interface PendingCapture {
  file: File;
  /** Object URL for the review preview — revoked when resolved. */
  url: string;
  /** Whether the file came from the camera input (Retake reopens the camera). */
  fromCamera: boolean;
}

export interface FieldEvidenceControlProps {
  /** Human label ("JRC", "Mass — Specimen 1", …) shown in tooltip + popover. */
  label: string;
  /** Required fields show the amber missing state when empty. */
  required: boolean;
  /** Field-scoped media already attached (completed uploads only). */
  items: ObservationMedia[];
  /**
   * Whether `items` reflects loaded truth. While false, `presumedHas` drives
   * the button state (lets grid cells show status from a bulk summary without
   * loading every row's media up front).
   */
  loaded: boolean;
  /** Optimistic has-evidence state used until `loaded` (bulk-summary driven). */
  presumedHas?: boolean;
  canEdit: boolean;
  /** Called when the popover/dialog opens — standalone wrappers lazy-fetch here. */
  onOpen?: () => void;
  upload: (file: File, onProgress?: (pct: number) => void) => Promise<void>;
  remove: (item: ObservationMedia) => Promise<void>;
  /** Compact 22px button for dense grid cells (default 28px). */
  dense?: boolean;
}

/**
 * The "Add Evidence" camera control (Option A of the 2026-07-24 mockups).
 * Three states: grey = optional & empty, amber dot = required & missing,
 * green + count = evidence attached. Opens a popover (full-width dialog on
 * phones) with native camera capture, upload/drag-drop, a review step
 * (Use photo / Retake) before anything is stored, thumbnails with lightbox,
 * and soft-delete removal.
 *
 * Props-driven; use FieldEvidenceButton (context) on single-test pages or
 * FieldEvidenceCellButton (standalone, lazy) inside batch grids.
 */
export const FieldEvidenceControl = ({
  label, required, items, loaded, presumedHas = false, canEdit, onOpen, upload, remove, dense = false,
}: FieldEvidenceControlProps) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<PendingCapture | null>(null);
  const [queue, setQueue] = useState<File[]>([]);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [viewer, setViewer] = useState<ObservationMedia | null>(null);
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [error, setError] = useState('');
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const has = loaded ? items.length > 0 : presumedHas;
  const missing = required && !has;

  const openReview = (files: File[], fromCamera: boolean) => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) {
      setError('Evidence must be a photo (JPG, PNG, HEIC).');
      return;
    }
    const [head, ...rest] = images;
    setQueue(rest);
    setPending({ file: head, url: URL.createObjectURL(head), fromCamera });
  };

  const resolvePending = (next: PendingCapture | null) => {
    if (pending) URL.revokeObjectURL(pending.url);
    setPending(next);
  };

  const advanceQueue = () => {
    if (queue.length > 0) {
      const [head, ...rest] = queue;
      setQueue(rest);
      resolvePending({ file: head, url: URL.createObjectURL(head), fromCamera: false });
    } else {
      resolvePending(null);
    }
  };

  const handleUsePhoto = async () => {
    if (!pending) return;
    setUploadPct(0);
    try {
      await upload(pending.file, setUploadPct);
      advanceQueue();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Upload failed — check your connection and try again.';
      setError(msg);
    } finally {
      setUploadPct(null);
    }
  };

  const handleRetake = () => {
    const fromCamera = pending?.fromCamera ?? false;
    setQueue([]);
    resolvePending(null);
    if (fromCamera) cameraInputRef.current?.click();
  };

  const handleDelete = (item: ObservationMedia) => {
    setConfirmDlg({
      open: true,
      title: 'Remove this evidence photo?',
      message: `"${item.FileName}" will no longer be attached to ${label}. The file itself is kept in the lab's storage archive.`,
      confirmLabel: 'Remove',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await remove(item);
        } catch {
          setError('Failed to remove — try again.');
        }
      },
    });
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) openReview(Array.from(e.dataTransfer.files), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (el: HTMLElement) => {
    onOpen?.();
    setAnchorEl(el);
  };

  const close = () => {
    setAnchorEl(null);
    setQueue([]);
    resolvePending(null);
  };

  // ── Button (the field decoration itself) ───────────────────────────────
  const stateColor = has ? theme.palette.success.main
    : missing ? theme.palette.warning.dark
    : theme.palette.text.disabled;
  const stateBg = has ? alpha(theme.palette.success.main, 0.1)
    : missing ? alpha(theme.palette.warning.main, 0.12)
    : alpha(theme.palette.text.disabled, 0.07);
  const tooltip = has
    ? `${label} — ${loaded ? `${items.length} evidence photo${items.length > 1 ? 's' : ''}` : 'evidence attached'}`
    : missing ? `${label} — evidence not added` : `Add evidence — ${label}`;
  const btnSize = dense ? 22 : 28;

  const content = (
    <Box
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      sx={{ p: 1.5, width: isPhone ? '100%' : 300 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.78rem', flex: 1 }}>
          Evidence · {label}
        </Typography>
        <IconButton size="small" onClick={close}><Close sx={{ fontSize: 16 }} /></IconButton>
      </Box>

      {/* Review step — the photo is shown full-frame before it is kept */}
      {pending ? (
        <Box>
          <Box component="img" src={pending.url} alt="Evidence preview"
            sx={{ width: '100%', maxHeight: isPhone ? '55vh' : 260, objectFit: 'contain',
              borderRadius: 1, backgroundColor: alpha('#000', 0.85) }} />
          {uploadPct !== null && (
            <LinearProgress variant="determinate" value={uploadPct} sx={{ height: 4, borderRadius: 2, mt: 0.5 }} />
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button size="small" variant="outlined" startIcon={<Replay sx={{ fontSize: 15 }} />}
              onClick={handleRetake} disabled={uploadPct !== null}
              sx={{ textTransform: 'none', flex: 1 }}>
              Retake
            </Button>
            <Button size="small" variant="contained" onClick={handleUsePhoto}
              disabled={uploadPct !== null}
              sx={{ textTransform: 'none', flex: 1.4 }}>
              {uploadPct !== null ? `Uploading ${uploadPct}%` : 'Use photo'}
            </Button>
          </Box>
          {queue.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {queue.length} more photo{queue.length > 1 ? 's' : ''} queued for review
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {!loaded ? (
            <LinearProgress sx={{ height: 3, borderRadius: 2, mb: 1 }} />
          ) : (
            <>
              {/* Attached photos */}
              {items.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                  {items.map((item) => (
                    <Box key={item.ObservationMediaKey} sx={{ position: 'relative' }}>
                      <Box component="img" src={item.thumbnailUrl || item.mediaUrl} alt={item.FileName}
                        onClick={() => setViewer(item)}
                        sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, cursor: 'pointer',
                          border: `1px solid ${alpha(theme.palette.success.main, 0.4)}` }} />
                      {canEdit && (
                        <IconButton size="small" onClick={() => handleDelete(item)}
                          sx={{ position: 'absolute', top: -8, right: -8, p: 0.25,
                            backgroundColor: 'background.paper', border: `1px solid ${alpha('#000', 0.15)}`,
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) } }}>
                          <Delete sx={{ fontSize: 13, color: theme.palette.error.main }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {items.length === 0 && (
                <Typography variant="caption" color={missing ? 'warning.dark' : 'text.secondary'}
                  sx={{ display: 'block', mb: 1, fontSize: '0.7rem', fontWeight: missing ? 600 : 400 }}>
                  {missing ? 'Evidence not added — required for this field.' : 'No evidence attached yet.'}
                </Typography>
              )}
            </>
          )}

          {canEdit && (
            <>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Button size="small" variant="contained" startIcon={<PhotoCamera sx={{ fontSize: 15 }} />}
                  onClick={() => cameraInputRef.current?.click()}
                  sx={{ textTransform: 'none', flex: 1, fontSize: '0.72rem' }}>
                  Take photo
                </Button>
                <Button size="small" variant="outlined" startIcon={<UploadFile sx={{ fontSize: 15 }} />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: 'none', flex: 1, fontSize: '0.72rem' }}>
                  Upload
                </Button>
              </Box>
              {!isPhone && (
                <Box sx={{
                  mt: 1, p: 1.25, textAlign: 'center', borderRadius: 1,
                  border: `1.5px dashed ${dragOver ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.3)}`,
                  backgroundColor: dragOver ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                  transition: 'all 0.15s ease',
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                    …or drag &amp; drop a photo here
                  </Typography>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* On iPad/iPhone capture="environment" opens the rear camera directly */}
      <input ref={cameraInputRef} type="file" hidden accept="image/*" capture="environment"
        onChange={(e) => { if (e.target.files?.length) openReview(Array.from(e.target.files), true); e.target.value = ''; }} />
      <input ref={fileInputRef} type="file" hidden multiple accept="image/*"
        onChange={(e) => { if (e.target.files?.length) openReview(Array.from(e.target.files), false); e.target.value = ''; }} />
    </Box>
  );

  return (
    <>
      <Tooltip title={tooltip}>
        <Badge
          badgeContent={has && loaded ? items.length : undefined}
          variant={missing || (has && !loaded) ? 'dot' : 'standard'}
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: has ? theme.palette.success.main : theme.palette.warning.main,
              color: '#fff', fontSize: '0.58rem', height: 14, minWidth: 14, px: has && loaded ? 0.4 : 0,
            },
          }}
        >
          <IconButton
            size="small"
            aria-label={tooltip}
            onClick={(e) => open(e.currentTarget)}
            sx={{
              width: btnSize, height: btnSize, borderRadius: 1.5,
              color: stateColor, backgroundColor: stateBg,
              border: `1px solid ${alpha(stateColor, 0.35)}`,
              '&:hover': { backgroundColor: alpha(stateColor, 0.18) },
            }}
          >
            <PhotoCamera sx={{ fontSize: dense ? 13 : 15 }} />
          </IconButton>
        </Badge>
      </Tooltip>

      {isPhone ? (
        <Dialog open={Boolean(anchorEl)} onClose={close} fullWidth
          PaperProps={{ sx: { borderRadius: 2, m: 1, width: '100%' } }}>
          {content}
        </Dialog>
      ) : (
        <Popover
          open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { borderRadius: 2, mt: 0.5 } } }}
        >
          {content}
        </Popover>
      )}

      {/* Lightbox */}
      <Dialog open={!!viewer} onClose={() => setViewer(null)} maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 2, backgroundColor: alpha('#000', 0.92) } }}>
        <IconButton onClick={() => setViewer(null)}
          sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 1 }}>
          <Close />
        </IconButton>
        <DialogContent sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {viewer && (
            <Box component="img" src={viewer.mediaUrl} alt={viewer.FileName}
              sx={{ maxWidth: '86vw', maxHeight: '82vh', objectFit: 'contain' }} />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" variant="filled" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </>
  );
};

interface FieldEvidenceButtonProps {
  /** Short field key from the test type's EVIDENCE_FIELDS entry. */
  fieldKey: string;
}

/**
 * Context-bound wrapper for single-test pages: reads everything from the
 * page's FieldEvidenceProvider. Renders nothing when the page has no provider
 * or the field is not evidence-eligible — safe in shared sections.
 */
export const FieldEvidenceButton = ({ fieldKey }: FieldEvidenceButtonProps) => {
  const ctx = useFieldEvidenceOptional();
  const field = useMemo(
    () => ctx?.fields.find((f) => f.fieldKey === fieldKey),
    [ctx, fieldKey],
  );
  const items = useMemo(() => (ctx && field ? ctx.itemsFor(fieldKey) : []), [ctx, field, fieldKey]);
  const upload = useCallback(
    (file: File, onProgress?: (pct: number) => void) => ctx!.upload(fieldKey, file, onProgress),
    [ctx, fieldKey],
  );
  if (!ctx || !field) return null;

  return (
    <FieldEvidenceControl
      label={field.label}
      required={field.required}
      items={items}
      loaded={ctx.loaded}
      canEdit={ctx.canEdit}
      upload={upload}
      remove={ctx.remove}
    />
  );
};
